<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t('kol.menu.payout_batches') || 'Payout Batches' }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button v-if="canWriteKOL" size="md" type="neutral" class="mr-2" @click="goFromFilter">
            <i class="fa fa-filter"></i> {{ $t('kol.payout_create_from_filter') || 'สร้างจาก Filter' }}
          </base-button>
          <base-button v-if="canWriteKOL" size="md" type="neutral" @click="goNewBatch">
            <i class="fa fa-plus"></i> {{ $t('add') || 'New batch' }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <div class="col-md-12 px-0">
        <card>
          <div class="row mb-2">
            <div class="col-md-3">
              <el-select v-model="statusFilter" @change="loadBatches" :placeholder="$t('all') || 'All'" class="w-100" clearable>
                <el-option label="Draft" value="draft"></el-option>
                <el-option label="Submitted" value="submitted"></el-option>
                <el-option label="Paid" value="paid"></el-option>
                <el-option label="Cancelled" value="cancelled"></el-option>
              </el-select>
            </div>
          </div>

          <el-table :data="batches" v-loading="loading" stripe @row-click="openBatch">
            <el-table-column label="#" width="80" prop="id"></el-table-column>
            <el-table-column label="Code" prop="code" width="160"></el-table-column>
            <el-table-column label="Status" prop="status" width="120">
              <template slot-scope="{row}">
                <span :class="statusClass(row.status)">{{ row.status }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Items" prop="item_count" align="right" width="80"></el-table-column>
            <el-table-column :label="$t('kol.payout_progress') || 'ความคืบหน้า'" width="160">
              <template slot-scope="{row}">
                <div v-if="row.item_count">
                  <div class="progress mb-1" style="height:6px">
                    <div class="progress-bar bg-success" :style="{ width: progressPct(row) + '%' }"></div>
                  </div>
                  <small>{{ row.paid_count || 0 }} / {{ row.item_count }}</small>
                </div>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column label="Gross" align="right" width="120">
              <template slot-scope="{row}">{{ fmt(row.total_gross) }}</template>
            </el-table-column>
            <el-table-column label="WHT" align="right" width="100">
              <template slot-scope="{row}">{{ fmt(row.total_wht) }}</template>
            </el-table-column>
            <el-table-column label="Net" align="right" width="120">
              <template slot-scope="{row}">{{ fmt(row.total_net) }}</template>
            </el-table-column>
            <el-table-column label="Created by" prop="created_by_name" min-width="140"></el-table-column>
            <el-table-column label="Created at" min-width="160">
              <template slot-scope="{row}">{{ fmtDate(row.created_at) }}</template>
            </el-table-column>
            <el-table-column :label="$t('kol.payout_last_updated') || 'อัพเดทล่าสุด'" min-width="160">
              <template slot-scope="{row}">{{ fmtDate(row.last_updated_at) }}</template>
            </el-table-column>
            <el-table-column label="" width="80" align="right">
              <template slot-scope="{row}">
                <button class="btn btn-link btn-sm" @click.stop="openBatch(row)">
                  <i class="fa fa-eye"></i>
                </button>
              </template>
            </el-table-column>
          </el-table>
        </card>
      </div>
    </div>

    <!-- New batch modal (create from selection) -->
    <modal :show.sync="showCreate" body-classes="p-0" modal-classes="modal-lg">
      <card type="secondary" header-classes="bg-white pb-5" body-classes="px-lg-5 py-lg-5" class="border-0 mb-0">
        <h3>New payout batch</h3>
        <p class="text-muted">Pick assignments that are <b>completed</b> and <b>unpaid</b>.</p>

        <el-table :data="eligibleAssignments" v-loading="loadingEligible" stripe @selection-change="onSelectionChange">
          <el-table-column type="selection" width="40"></el-table-column>
          <el-table-column label="Code" prop="code" width="140"></el-table-column>
          <el-table-column label="KOL" prop="creator_name" min-width="160"></el-table-column>
          <el-table-column label="Tax" prop="tax_type" width="80"></el-table-column>
          <el-table-column label="Amount" align="right" width="120">
            <template slot-scope="{row}">{{ fmt(row.payment_amount) }}</template>
          </el-table-column>
        </el-table>

        <div class="row mt-3">
          <div class="col-md-12">
            <base-input label="Note" v-model="newNote"></base-input>
          </div>
        </div>

        <div class="text-right mt-3">
          <button class="btn btn-link" @click="showCreate = false">Close</button>
          <button class="btn btn-primary" :disabled="!selectedIds.length || saving" @click="submitNewBatch">
            Create draft ({{ selectedIds.length }})
          </button>
        </div>
      </card>
    </modal>

    <!-- Create from filter modal -->
    <modal :show.sync="showFromFilter" body-classes="p-0" modal-classes="modal-md">
      <card type="secondary" header-classes="bg-white pb-5" body-classes="px-lg-5 py-lg-5" class="border-0 mb-0">
        <h3>{{ $t('kol.payout_create_from_filter') || 'สร้างจาก Filter' }}</h3>
        <p class="text-muted">สร้างรอบจ่ายเงินจาก criteria ที่กำหนด (งาน completed + unpaid)</p>

        <div class="form-group">
          <label class="form-control-label">{{ $t('kol.payout_filter_creator') || 'Creator' }}</label>
          <el-select v-model="ff.creator_id" clearable filterable :placeholder="$t('all') || 'All'" class="w-100">
            <el-option v-for="c in ffCreators" :key="c.id" :label="c.name" :value="c.id"></el-option>
          </el-select>
        </div>

        <div class="form-group">
          <label class="form-control-label">Staff</label>
          <el-select v-model="ff.staff_id" clearable filterable :placeholder="$t('all') || 'All'" class="w-100">
            <el-option v-for="s in ffStaff" :key="s.id" :label="s.name || s.nickname || s.id" :value="s.id"></el-option>
          </el-select>
        </div>

        <div class="form-group">
          <label class="form-control-label">{{ $t('kol.payout_filter_date_range') || 'ช่วงวันที่งาน' }}</label>
          <el-date-picker
            v-model="ff.dateRange"
            type="daterange"
            range-separator="-"
            start-placeholder="จาก"
            end-placeholder="ถึง"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            class="w-100"
          ></el-date-picker>
        </div>

        <div class="form-group">
          <label class="form-control-label">Note (optional)</label>
          <textarea class="form-control" rows="2" v-model="ff.note"></textarea>
        </div>

        <div class="text-right mt-3">
          <button class="btn btn-link" @click="showFromFilter = false" :disabled="ffSaving">Close</button>
          <button class="btn btn-primary" :disabled="ffSaving" @click="submitFromFilter">
            <i class="fa fa-check"></i> {{ ffSaving ? 'กำลังสร้าง...' : 'สร้าง' }}
          </button>
        </div>
      </card>
    </modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, DatePicker } from "element-ui";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import KOLProvider from "@/resource/kol";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";

const kol = new KOLProvider();

export default {
  components: {
    RouteBreadCrumb,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
  },
  data() {
    return {
      loading: false,
      saving: false,
      batches: [],
      statusFilter: "",
      showCreate: false,
      newNote: "",
      eligibleAssignments: [],
      loadingEligible: false,
      selectedIds: [],
      showFromFilter: false,
      ffSaving: false,
      ff: { creator_id: null, staff_id: null, dateRange: null, note: "" },
      ffCreators: [],
      ffStaff: [],
    };
  },
  computed: {
    ...mapGetters({
      authUser: "Auth/getUser",
    }),
    roles() {
      return (this.authUser && this.authUser.roles) || {};
    },
    canWriteKOL() {
      return (this.roles.kol || 0) >= 2 || this.authUser.group === 99;
    },
    canPayKOL() {
      return (this.roles.kol_financial || 0) >= 2 || this.authUser.group === 99;
    },
  },
  mounted() {
    this.loadBatches();
  },
  methods: {
    fmt(v) {
      const n = Number(v || 0);
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fmtDate(d) { return d ? String(d).substring(0, 19).replace("T", " ") : "-"; },
    statusClass(s) {
      return {
        draft: "badge badge-secondary",
        submitted: "badge badge-info",
        paid: "badge badge-success",
        cancelled: "badge badge-warning",
      }[s] || "badge";
    },
    progressPct(row) {
      if (!row.item_count) return 0;
      return Math.round(((row.paid_count || 0) / row.item_count) * 100);
    },
    async loadBatches() {
      this.loading = true;
      try {
        const res = await kol.PayoutBatchList({ status: this.statusFilter || undefined });
        this.batches = (res.data && res.data.Data && res.data.Data[0]) || [];
      } catch (e) { console.log(e); }
      this.loading = false;
    },
    openBatch(row) {
      this.$router.push("/kol/payout-batches/" + row.id);
    },
    async goNewBatch() {
      this.newNote = "";
      this.selectedIds = [];
      this.eligibleAssignments = [];
      this.showCreate = true;
      this.loadingEligible = true;
      try {
        const res = await kol.AssignmentList({ status: "completed", payment_status: "unpaid", per_page: 200 });
        const data = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.eligibleAssignments = data.items || data.Items || data || [];
      } catch (e) { console.log(e); }
      this.loadingEligible = false;
    },
    onSelectionChange(rows) { this.selectedIds = rows.map(r => r.id); },
    async submitNewBatch() {
      if (!this.selectedIds.length) return;
      this.saving = true;
      try {
        await kol.PayoutBatchCreate({ assignment_ids: this.selectedIds, note: this.newNote });
        this.showCreate = false;
        this.loadBatches();
        swal.fire({ title: "Draft batch created", icon: "success", buttonsStyling: false, confirmButtonClass: "btn btn-success btn-fill" });
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || "Failed";
        swal.fire({ title: msg, icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
      }
      this.saving = false;
    },
    async goFromFilter() {
      this.ff = { creator_id: null, staff_id: null, dateRange: null, note: "" };
      this.showFromFilter = true;
      // Load creators and staff for dropdowns if not already loaded
      if (!this.ffCreators.length) {
        try {
          const res = await kol.CreatorList({ per_page: 500 });
          const data = (res.data && res.data.Data && res.data.Data[0]) || {};
          this.ffCreators = data.items || data.Items || data || [];
        } catch (e) { console.log(e); }
      }
      if (!this.ffStaff.length) {
        try {
          const res = await kol.StaffList({ per_page: 500 });
          const data = (res.data && res.data.Data && res.data.Data[0]) || {};
          this.ffStaff = data.items || data.Items || data || [];
        } catch (e) { console.log(e); }
      }
    },
    async submitFromFilter() {
      this.ffSaving = true;
      try {
        const body = {
          status_list: ["completed"],
          payment_status: "unpaid",
        };
        if (this.ff.creator_id) body.creator_id = this.ff.creator_id;
        if (this.ff.staff_id) body.staff_id = this.ff.staff_id;
        if (this.ff.dateRange && this.ff.dateRange[0]) body.date_from = this.ff.dateRange[0];
        if (this.ff.dateRange && this.ff.dateRange[1]) body.date_to = this.ff.dateRange[1];
        if (this.ff.note) body.note = this.ff.note;

        const res = await kol.PayoutBatchCreateFromFilter(body);
        const batch = (res.data && res.data.Data && res.data.Data[0] && res.data.Data[0].batch) || null;
        this.showFromFilter = false;
        if (batch && batch.id) {
          this.$router.push("/kol/payout-batches/" + batch.id);
        } else {
          this.loadBatches();
          swal.fire({ title: "Batch created", icon: "success", buttonsStyling: false, confirmButtonClass: "btn btn-success btn-fill" });
        }
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || "Failed";
        swal.fire({ title: msg, icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
      }
      this.ffSaving = false;
    },
  },
};
</script>
